'use client'

import { cn } from '@/utils/utils'

import { useId } from 'react'

export type VueMarkState =
  | 'idle'
  | 'thinking'
  | 'streaming'
  | 'listening'
  | 'speaking'
  | 'error'

export interface VueMarkProps {
  /**
   * Rendered edge length — a px number, or any CSS length (e.g. '0.95em').
   * Omit it to let a sizing class on the mark decide instead; with neither,
   * the SVG falls back to the 300×150 CSS default.
   */
  size?: number | string
  /** Live state — drives the CSS animation. */
  state?: VueMarkState
  /** Paint `currentColor` instead of the spark gradient */
  mono?: boolean
  /** Accessible name. `null` → `aria-hidden`, for use beside a visible wordmark. */
  label?: string | null
  className?: string
}

/**
 * The single shard. The other two shards are 120° and 240° rotations of this
 * one about the centroid `(12, 12.6)`, derived with `transform` so the
 * three-fold symmetry can never drift — there is exactly one path to maintain.
 */
const SHARD = 'M9.73 5.33 12.07 6.69 5.74 14.94Z'
const SHARD_ROTATIONS = [0, 120, 240]

/**
 * Vue product mark — three tapered shards around a triangular void
 *
 */
export function VueMark({
  size,
  state = 'idle',
  mono = false,
  label = 'Vue',
  className
}: VueMarkProps) {
  const gradientId = useId()
  // error renders muted and inert; mono paints the host's text color. Both
  // skip the gradient (and its <defs>).
  const useGradient = !mono && state !== 'error'
  const fill = useGradient ? `url(#${gradientId})` : 'currentColor'

  return (
    // viewBox is centered on the centroid (12, 12.6) and sized to the rotation
    // envelope (spin radius ~7.62), not the 24×24 authoring box — so `size` maps
    // to true visual size, matching a Lucide icon beside it. Path data unchanged.
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="4 4.6 16 16"
      width={size}
      height={size}
      // Inline, so an explicit `size` survives a container that sizes the SVGs
      // inside it
      style={size === undefined ? undefined : { width: size, height: size }}
      className={cn(
        'vue-mark',
        `vue-mark--${state}`,
        state === 'error' && 'text-muted-foreground',
        className
      )}
      role={label ? 'img' : undefined}
      aria-label={label ?? undefined}
      aria-hidden={label ? undefined : true}
    >
      {useGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="4"
            y1="4"
            x2="20"
            y2="20"
          >
            <stop offset="0" stopColor="var(--ai-gradient-from)" />
            <stop offset="0.55" stopColor="var(--ai-gradient-via)" />
            <stop offset="1" stopColor="var(--ai-gradient-to)" />
          </linearGradient>
        </defs>
      )}
      <g className="vue-mark-shards" fill={fill}>
        {/* Rotations sit on wrappers CSS never targets: a CSS `transform` on a
            shard would replace its SVG one and stack all three. */}
        {SHARD_ROTATIONS.map((deg) => (
          <g key={deg} transform={`rotate(${deg} 12 12.6)`}>
            <path className="vue-mark-shard" d={SHARD} />
          </g>
        ))}
      </g>
    </svg>
  )
}
